import { type FC, type PropsWithChildren, useMemo } from 'react';
import TypesenseInstantSearchAdapter from 'typesense-instantsearch-adapter';
import { type SearchClient } from 'algoliasearch/lite';
import { useAuth0 } from '@auth0/auth0-react';
import { SearchClientContext } from '@/context/SearchClientContext';
import { useAccount } from '@/hooks/data/account/useAccount';
import { SearchProvider } from '@/context/SearchContext';
import { useCurrentPage } from '@/hooks/useCurrentPage';

export const TypesenseProvider: FC<PropsWithChildren> = ({ children }) => {
  const { profile } = useAccount();
  const { isAuthenticated, isLoading } = useAuth0();
  const { isPublicSharePage } = useCurrentPage();

  const searchClient = useMemo(() => {
    if (!profile?.search) {
      return null;
    }
    const { host, port, protocol, apiKey, queryBy, infix } = profile.search;
    if (!host || !apiKey) {
      return null;
    }
    const adapter = new TypesenseInstantSearchAdapter({
      server: {
        apiKey,
        nodes: [{ host, port, protocol }],
      },
      additionalSearchParameters: {
        query_by: queryBy,
        drop_tokens_threshold: 0,
        // Per-field infix list aligned to query_by, supplied by the backend search
        // profile and enforced by the scoped key. A scalar 'fallback' would force
        // the infix index on fields without one (e.g. description) -> 400.
        infix,
        max_candidates: 100,
      },
    });
    return adapter.searchClient as unknown as SearchClient;
  }, [profile]);

  if (isPublicSharePage) {
    return children;
  }

  if (!isAuthenticated || isLoading || !profile || !searchClient || !profile.search.searchIndexes) {
    return null;
  }

  return (
    <SearchClientContext.Provider value={{ searchClient, searchIndexes: profile.search.searchIndexes }}>
      <SearchProvider>{children}</SearchProvider>
    </SearchClientContext.Provider>
  );
};
